import React, { useState, useMemo } from 'react';

const PAISES = [
  { codigo: '55', bandeira: '🇧🇷', nome: 'Brasil', mascara: '(XX) XXXXX-XXXX', max: 11 },
  { codigo: '351', bandeira: '🇵🇹', nome: 'Portugal', mascara: 'XXX XXX XXX', max: 9 },
  { codigo: '1', bandeira: '🇺🇸', nome: 'EUA / Canadá', mascara: '(XXX) XXX-XXXX', max: 10 },
  { codigo: '54', bandeira: '🇦🇷', nome: 'Argentina', mascara: 'XX XXXX-XXXX', max: 10 },
  { codigo: '598', bandeira: '🇺🇾', nome: 'Uruguai', mascara: 'XXXX XXXX', max: 8 },
  { codigo: '595', bandeira: '🇵🇾', nome: 'Paraguai', mascara: 'XXX XXX XXX', max: 9 },
  { codigo: '56', bandeira: '🇨🇱', nome: 'Chile', mascara: 'X XXXX XXXX', max: 9 },
  { codigo: '34', bandeira: '🇪🇸', nome: 'Espanha', mascara: 'XXX XX XX XX', max: 9 },
  { codigo: '39', bandeira: '🇮🇹', nome: 'Itália', mascara: 'XXX XXX XXXX', max: 10 },
  { codigo: '44', bandeira: '🇬🇧', nome: 'Reino Unido', mascara: 'XXXX XXXXXX', max: 10 },
];

function detectarPais(valor) {
  const digits = (valor || '').replace(/\D/g, '');
  // Tenta match do código mais longo primeiro
  const sorted = [...PAISES].sort((a, b) => b.codigo.length - a.codigo.length);
  for (const p of sorted) {
    if (digits.startsWith(p.codigo)) return p;
  }
  return PAISES[0]; // Brasil como fallback
}

function formatarNumero(digits, pais) {
  if (!digits) return '';
  const d = digits.slice(0, pais.max);

  switch (pais.codigo) {
    case '55': // Brasil (XX) XXXXX-XXXX
      if (d.length <= 2) return `(${d}`;
      if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
      return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    case '1': // EUA (XXX) XXX-XXXX
      if (d.length <= 3) return `(${d}`;
      if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    case '351': // Portugal XXX XXX XXX
    case '595': // Paraguai
      return d.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
    case '54': // Argentina XX XXXX-XXXX
      if (d.length <= 2) return d;
      if (d.length <= 6) return `${d.slice(0, 2)} ${d.slice(2)}`;
      return `${d.slice(0, 2)} ${d.slice(2, 6)}-${d.slice(6)}`;
    default: // Genérico: grupos de 3-4
      return d.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  }
}

const TelefoneInput = ({ value, onChange, style, placeholder, ...rest }) => {
  const [dropdownAberto, setDropdownAberto] = useState(false);

  const paisAtual = useMemo(() => detectarPais(value), [value]);

  const digitsCompleto = (value || '').replace(/\D/g, '');
  const digitsSemCodigo = digitsCompleto.startsWith(paisAtual.codigo)
    ? digitsCompleto.slice(paisAtual.codigo.length)
    : digitsCompleto;
  const valorFormatado = formatarNumero(digitsSemCodigo, paisAtual);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, paisAtual.max);
    onChange(`${paisAtual.codigo}${raw}`);
  };

  const selecionarPais = (pais) => {
    // Mantém os dígitos locais ao trocar de país
    onChange(`${pais.codigo}${digitsSemCodigo.slice(0, pais.max)}`);
    setDropdownAberto(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {/* Seletor de país */}
        <button
          type="button"
          onClick={() => setDropdownAberto(!dropdownAberto)}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '8px 10px', border: '2px solid #f0ece8', borderRight: 'none',
            borderRadius: '10px 0 0 10px', background: '#faf7f5', cursor: 'pointer',
            fontSize: 13, color: '#555', fontWeight: 600, whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontSize: 16 }}>{paisAtual.bandeira}</span>
          +{paisAtual.codigo}
          <span style={{ fontSize: 10, color: '#bbb' }}>▼</span>
        </button>

        {/* Input */}
        <input
          type="tel"
          value={valorFormatado}
          onChange={handleChange}
          placeholder={placeholder || paisAtual.mascara.replace(/X/g, '9')}
          style={{ ...style, borderRadius: '0 10px 10px 0', flex: 1 }}
          {...rest}
        />
      </div>

      {/* Dropdown de países */}
      {dropdownAberto && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setDropdownAberto(false)} />
          <div style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 4,
            background: '#fff', borderRadius: 10, boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
            border: '1px solid #eee', zIndex: 100, width: 240, maxHeight: 250, overflow: 'auto',
          }}>
            {PAISES.map(p => (
              <div
                key={p.codigo}
                onClick={() => selecionarPais(p)}
                style={{
                  padding: '8px 12px', cursor: 'pointer', fontSize: 14,
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: p.codigo === paisAtual.codigo ? '#fef3f0' : '#fff',
                  borderBottom: '1px solid #f8f8f8',
                }}
              >
                <span style={{ fontSize: 18 }}>{p.bandeira}</span>
                <span style={{ flex: 1 }}>{p.nome}</span>
                <span style={{ color: '#999', fontSize: 13 }}>+{p.codigo}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default TelefoneInput;
